import { useState, useMemo, useEffect } from 'react';
import LanguageContext, { LanguageContextValue } from '../../contexts/language';
import {
  Language,
  TranslationNestedRecord,
  TranslationRecord,
  isTranslationRecord,
} from '../../types/language';
import {
  retrieveFromLocalStorage,
  saveInLocalStorage,
} from '../../utils/storage';
import translatedStrings from '../../assets/translated-strings/source.json';

const CURRENT_LANGUAGE_KEY = 'current-language';

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [language, setLanguage] = useState<Language>(
    () =>
      retrieveFromLocalStorage<Language>(CURRENT_LANGUAGE_KEY, {
        serialized: false,
        checkDataType: (data): data is Language =>
          Object.values(Language).includes(data as Language),
      }) ?? Language.EN,
  );

  const value = useMemo<LanguageContextValue>(() => {
    const translations: Record<string, string> = {};

    // The whole translations object is flattened and only the proper translations obtained
    const stack: [string, TranslationNestedRecord | TranslationRecord][] =
      Object.entries(translatedStrings as TranslationNestedRecord);

    for (let nextValue = stack.pop(); nextValue; nextValue = stack.pop()) {
      const [key, record] = nextValue;

      if (isTranslationRecord(record)) {
        translations[key.toLowerCase()] = record[language];
        continue;
      }

      const newEntries = Object.entries(record).map<
        [string, TranslationNestedRecord | TranslationRecord]
      >(([recordKey, innerRecord]) => [
        `${key.toLowerCase()}.${recordKey}`,
        innerRecord,
      ]);

      stack.push(...newEntries);
    }

    return {
      language,
      setLanguage: language => setLanguage(language),
      t: (stringId, content = {}) => {
        const translation = translations[stringId.toLowerCase()];

        if (!translation) {
          return stringId;
        }

        /* Returns the translation replacing any content (with format "{{KEY}}") if needed */
        return Object.entries(content).reduce(
          (acc, [key, value]) => acc.replace(`{{${key.toUpperCase()}}}`, value),
          translation,
        );
      },
    };
  }, [language]);

  useEffect(() => {
    saveInLocalStorage(CURRENT_LANGUAGE_KEY, language, { serialize: false });
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
